import React, { MouseEventHandler, useEffect, useReducer } from 'react';
import TruncateMarkup from 'react-truncate-markup';
import { Image } from '../../../commonTypes/image';
import { TextLink } from '../../../commonTypes/links';
import colors from '../../../helpers/colors';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import ReadTimeShape from '../ReadTimeShape';
import {
  Body,
  BodyWrapper,
  ContentWrapper,
  Heading,
  ImageIEFallback,
  ImageWrapper,
  ReadMoreButton,
  ReadMoreIcon,
  ReadTime,
  ReadTimeIcon,
  ReadTimeWrapper,
  RelatedContentCardContainer,
  RelatedContentCardContainerProps,
  StyledImage,
  Subheading,
} from './styles';

export type RelatedContentCardProps = {
  url: string;
  onClick?: MouseEventHandler;
  image?: Image;
  heading: string;
  subheading?: string;
  body?: string;
  readTime?: number;
  readTimeText?: string;
  readTimeIcon?: string;
  readMore?: TextLink;
  maxLines?: number;
} & RelatedContentCardContainerProps &
  ComponentProps;

const RelatedContentCard: React.FC<RelatedContentCardProps> = ({
  url,
  onClick,
  image,
  heading,
  subheading,
  body,
  readTime,
  readTimeText = 'MINS READ',
  readTimeIcon = '',
  readMore,
  maxLines,
  disabled,
  displayBackground,
  ...props
}) => {
  /* 
  By default, Truncate runs before fonts have loaded, so if the primary font is larger than the backup and also needs to load in, 
  the number of characters calculation will be done with the smaller backup font, 
  causing there to be too many characters in the primary font.
  
  Instead, we can force a rerender after the fonts are loaded by changing the key of the container
  */
  const [rerenderKey, triggerRerender] = useReducer((i) => i + 1, 0);
  useEffect(() => {
    document.fonts?.ready.then(() => {
      triggerRerender();
    });
  }, []);

  return (
    <RelatedContentCardContainer
      key={rerenderKey}
      {...componentProps(props)}
      onClick={onClick}
      title={heading}
      href={url}
      disabled={disabled}
      displayBackground>
      {image && (
        <ImageWrapper>
          <StyledImage image={image} focusable={false} />
          <ImageIEFallback url={image.url} role="img" aria-label={image.altText} />
        </ImageWrapper>
      )}
      <ContentWrapper>
        <Heading>
          <TruncateMarkup lines={2}>
            <div data-testid="heading">{heading}</div>
          </TruncateMarkup>
        </Heading>
        {subheading && <Subheading>{subheading}</Subheading>}
        {body && (
          <BodyWrapper>
            <Body maxLines={maxLines}>
              <TruncateMarkup lines={maxLines ?? 2}>
                <div data-testid="body">{body}</div>
              </TruncateMarkup>
            </Body>
          </BodyWrapper>
        )}
      </ContentWrapper>
      {readMore && readMore.url && (
        <ReadMoreButton onClick={readMore.onClick} color={colors.core01}>
          {readMore.text}
          <ReadMoreIcon name="chevron" size="small" color={colors.core01} />
        </ReadMoreButton>
      )}
      {readTime && (
        <ReadTimeWrapper>
          <ReadTimeShape>
            {readTimeIcon && (
              <ReadTimeIcon name={readTimeIcon} size="small" color={colors.core01} />
            )}
            <ReadTime>{`${readTime} ${readTimeText}`}</ReadTime>
          </ReadTimeShape>
        </ReadTimeWrapper>
      )}
    </RelatedContentCardContainer>
  );
};

export default applyComponentTheme(RelatedContentCard, 'RelatedContentCard');
